import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import styled from '@emotion/styled';

import { formatNaira } from '../../utils/utils';
import ChevronRightBoxIcon from '../Icons/ChevronRightBoxIcon';
import DefaultCardStatsIcon from '../Icons/DefaultCardStatsIcon';
import DownChartIcon from '../Icons/DownChartIcon';
import UpChartIcon from '../Icons/UpChartIcon';
import { Modal } from '../Modal/Modal';

interface CardProps {
  children?: React.ReactNode;
  padding?: string;
  height?: string;
}
const CardRoot = ({ children, padding, height }: CardProps) => {
  return (
    <StyledCardRoot padding={padding} height={height}>
      {children}
    </StyledCardRoot>
  );
};

interface CardHeaderProps {
  children: React.ReactNode;
  padding?: string;
  alignSelf?: string;
  color?: string;
  fontSize?: string;
}

const CardHeader = ({
  children,
  padding,
  alignSelf,
  color,
  fontSize,
}: CardHeaderProps) => {
  return (
    <StyledCardHeader
      padding={padding}
      alignSelf={alignSelf}
      color={color}
      fontSize={fontSize}
    >
      {children}
    </StyledCardHeader>
  );
};

const CardBody = ({ children }: CardProps) => {
  return <StyledCardBody>{children}</StyledCardBody>;
};

const CardBodyText = ({ children }: CardProps) => {
  return <StyledCardBodyText>{children}</StyledCardBodyText>;
};

const CardBodyControlledText = ({ children }: CardProps) => {
  return (
    <StyledCardBodyControlledText>{children}</StyledCardBodyControlledText>
  );
};

interface CardFooterNavProps {
  children: React.ReactNode;
  path: string;
}

const CardFooterNav = ({ children, path }: CardFooterNavProps) => {
  const navigate = useNavigate();

  return (
    <StyledCardFooterNav onClick={() => navigate(path)}>
      {children} <ChevronRightBoxIcon />
    </StyledCardFooterNav>
  );
};

interface CardFooterProps {
  children: React.ReactNode;
}

const CardFooter = ({ children }: CardFooterProps) => {
  return <StyledCardFooter>{children}</StyledCardFooter>;
};

interface CardLargeNumberProps {
  children: React.ReactNode;
  type?: 'default' | 'currency';
}
const CardLargeNumber: React.FC<CardLargeNumberProps> = ({
  children,
  type = 'default',
}) => {
  return (
    <StyledCardLargeNumber
    // className={type === 'currency' ? 'currency' : ''}
    >
      {type === 'currency' ? formatNaira(Number(children) || 0) : children}
    </StyledCardLargeNumber>
  );
};

interface CardStatsProps {
  children?: React.ReactNode;
  num?: number;
  trend?: 'up' | 'down' | 'constant';
  marginTop?: string;
}

const CardStats: React.FC<CardStatsProps> = ({
  children,
  num,
  trend,
  marginTop,
}) => {
  return (
    <StyledCardStats marginTop={marginTop}>
      {children}
      {trend === 'constant' && <DefaultCardStatsIcon />}
      {trend === 'down' && <DownTrend num={num || 0} />}
      {trend === 'up' && <UpTrend num={num || 0} />}
    </StyledCardStats>
  );
};

interface TrendProps {
  num: number;
}

const UpTrend: React.FC<TrendProps> = ({ num }) => {
  return (
    <Trend>
      <UpChartIcon /> {num}%
    </Trend>
  );
};

const DownTrend: React.FC<TrendProps> = ({ num }) => {
  return (
    <Trend className={'down'}>
      <DownChartIcon /> {num}%
    </Trend>
  );
};

interface ShowModalBtnProps {
  children: React.ReactNode;
  buttonLabel: string;
}

const ShowModalBtn: React.FC<ShowModalBtnProps> = ({
  children,
  buttonLabel,
}) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <StyledShowModalBtn onClick={() => setShowModal(true)}>
        {buttonLabel}
      </StyledShowModalBtn>
      <Modal showModal={showModal} setShowModal={setShowModal}>
        {children}
      </Modal>
    </>
  );
};

interface AddLightsProps {
  children: React.ReactNode;
  light: any;
}

const AddLights = ({ children, light }: AddLightsProps) => {
  return (
    <AddLightsStyle>
      <div>
        <AddLightsStyleRelative>{light}</AddLightsStyleRelative>
        {/* {light} */}
        <div>{children}</div>
      </div>
    </AddLightsStyle>
  );
};

const Card = {
  Root: CardRoot,
  Header: CardHeader,
  Body: CardBody,
  LargeNumber: CardLargeNumber,
  StatsArea: CardStats,
  Modal: ShowModalBtn,
  BodyText: CardBodyText,
  AddLights: AddLights,
  ControlledBodyText: CardBodyControlledText,
  FooterNav: CardFooterNav,
  Footer: CardFooter,
};

export default Card;

interface StyledCardRootProps {
  padding?: string;
  height?: string;
}

const AddLightsStyle = styled.div`
  justify-content: center;
  align-items: center;
  display: grid;

  & > * {
    grid-row: 1;
    grid-column: 1;
    position: relative;
  }
`;

const AddLightsStyleRelative = styled.div`
  position: absolute;
  left: -100px;
  top: -2px;
`;

const StyledCardRoot = styled.div<StyledCardRootProps>`
  background-color: white;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: ${({ padding }) => (padding ? padding : '8px')};
  border-radius: 20px;
  width: 100%;
  display: flex;
  gap: 8px;
  flex-direction: column;
  height: ${({ height }) => (height ? height : 'auto')};
  /* justify-content: space-between; */
`;

interface StyledCardHeaderProps {
  padding?: string;
  alignSelf?: string;
  color?: string;
  fontSize?: string;
}

const StyledCardHeader = styled.div<StyledCardHeaderProps>`
  color: ${({ color }) => (color ? color : '#6b7280')};
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${({ padding }) => (padding ? padding : '0px')};
  font-size: ${({ fontSize }) => (fontSize ? fontSize : '14px')};
  font-weight: 400;
  gap: 10px;
  align-self: ${({ alignSelf }) => (alignSelf ? alignSelf : 'center')};
`;

const StyledCardBody = styled.div`
  /* border: 1px solid blue; */
`;

const StyledCardBodyText = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: #6b7280;
`;

const StyledCardBodyControlledText = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: #9ca3af;
  margin-top: 12px;
  align-self: center;
`;

const StyledCardFooterNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 400;
  color: rgba(35, 34, 34, 0.5);
  cursor: pointer;
  padding-top: 12px;

  & > svg {
    transition: all 0.3s ease;
  }

  &:hover {
    & > svg {
      transform: translateX(4px);
    }
  }
`;

const StyledCardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 400;
  color: #71748c;
  text-align: center;
  justify-content: center;
  /* padding-bottom: 12px; */
`;

const StyledCardLargeNumber = styled.div`
  font-size: 24px;
  line-height: 24px;
  font-weight: 500;
  color: #232222;
  align-self: center;
  text-align: center;

  /* &.currency {
    font-size: 32px;
  } */
`;

interface StyledCardStatsProps {
  marginTop?: string;
}

const StyledCardStats = styled.div<StyledCardStatsProps>`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding: 0px 34px;
  color: #6b7280;
  /* margin-top: 30px; */
  margin-top: ${({ marginTop }: StyledCardStatsProps) =>
    marginTop ? marginTop : 'auto'};
`;

const Trend = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 400;
  color: #00b73f;

  &.down {
    color: #ff0500;
  }
`;

const StyledShowModalBtn = styled.button`
  cursor: pointer;
  width: fit-content;
  align-self: center;
  border-radius: 5px;
  padding: 12px 40px;
  background-color: #232222;
  color: white;
  border: none;
  font-size: 14px;
  font-weight: 500;
  margin-top: 38px;
`;
